import {
    AlertTriangle,
    ArrowDownUp,
    ArrowRight,
    ChevronLeft,
    ChevronRight,
    ClipboardCheck,
    LoaderCircle,
    TrendingUp,
    Users,
    Wallet,
} from 'lucide-react'
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './StoreLeaveBalanceSummaryScreen.module.css'

const SORT_OPTIONS = [
    { value: 'staffName', label: 'Name' },
    { value: 'plRemaining', label: 'PL Balance' },
    { value: 'clRemaining', label: 'CL Balance' },
    { value: 'slRemaining', label: 'SL Balance' },
    { value: 'totalLeaveRemaining', label: 'Total Leave' },
]

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

const toNumber = (value, fallback = 0) => {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : fallback
}

const formatAmount = (amount) => {
    const value = toNumber(amount, 0)
    return value.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
}

const StoreLeaveBalanceSummaryScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [summaryData, setSummaryData] = useState(null)
    const [isLoading, setIsLoading] = useState(true)
    const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
    const [sortBy, setSortBy] = useState('staffName')
    const [sortOrder, setSortOrder] = useState('asc')

    const fetchLeaveBalanceSummary = useCallback(async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) {
            setIsLoading(false)
            return
        }

        try {
            setIsLoading(true)

            const response = await fetch(BASE_URL_API + 'getStoreLeaveBalanceSummary', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    year: selectedYear,
                    sortBy,
                    sortOrder,
                }),
            })

            if (!response.ok) {
                toast.error('Failed to load leave summary.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                setSummaryData(respo.response)
                return
            }

            if (respo?.message === 'tokenExpired') {
                authCtx.logout()
                return
            }

            toast.error(respo?.message || 'Failed to load summary.')
        } catch (error) {
            console.error('getStoreLeaveBalanceSummary error:', error)
            toast.error('Network error.')
        } finally {
            setIsLoading(false)
        }
    }, [activeStore?.storeIdentifier, authCtx, selectedYear, sortBy, sortOrder])

    useEffect(() => {
        fetchLeaveBalanceSummary()
    }, [fetchLeaveBalanceSummary])

    const handleSortChange = (newSortBy) => {
        if (newSortBy === sortBy) {
            setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
            return
        }
        setSortBy(newSortBy)
        setSortOrder('desc')
    }

    const handleViewLeaveCard = (staff) => {
        navigate(`/payroll/staff-leave-card/${staff.staffIdentifier}`, {
            state: {
                staffIdentifier: staff.staffIdentifier,
                staffName: staff.staffName,
            },
        })
    }

    const handleCreditLeave = (staff) => {
        const currentBalances = [
            { leaveType: 'CL', availableBalance: toNumber(staff?.clRemaining, 0) },
            { leaveType: 'SL', availableBalance: toNumber(staff?.slRemaining, 0) },
            { leaveType: 'PL', availableBalance: toNumber(staff?.plRemaining, 0) },
            { leaveType: 'CO', availableBalance: toNumber(staff?.coRemaining, 0) },
        ]

        navigate(`/payroll/manual-leave-adjustment/${staff?.staffIdentifier}`, {
            state: {
                staffName: staff?.staffName,
                preselectedLeaveType: 'CL',
                currentBalances,
            },
        })
    }

    const openInitializeAll = () => {
        navigate('/payroll/setup/leave-balance-init')
    }

    const currentYear = new Date().getFullYear()
    const canGoPrevYear = selectedYear > 2020
    const canGoNextYear = selectedYear < currentYear + 1

    const staffList = useMemo(() => {
        return Array.isArray(summaryData?.staff) ? summaryData.staff : []
    }, [summaryData?.staff])

    const selectedSortLabel = useMemo(() => {
        return SORT_OPTIONS.find((item) => item.value === sortBy)?.label || 'Name'
    }, [sortBy])

    return (
        <div className={styles.page}>
            <div className={styles.header}>
                <button className={styles.backBtn} type="button" onClick={() => navigate(-1)}>
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <h1 className={styles.headerTitle}>Leave Balance Overview</h1>
                    <p className={styles.headerSub}>Track staff leave availability and PL encashment exposure</p>
                </div>
            </div>

            <div className={styles.yearSelector}>
                <button
                    type="button"
                    className={styles.yearArrowBtn}
                    onClick={() => setSelectedYear((prev) => prev - 1)}
                    disabled={!canGoPrevYear || isLoading}
                >
                    <ChevronLeft size={16} />
                </button>
                <span className={styles.yearLabel}>{selectedYear}</span>
                <button
                    type="button"
                    className={styles.yearArrowBtn}
                    onClick={() => setSelectedYear((prev) => prev + 1)}
                    disabled={!canGoNextYear || isLoading}
                >
                    <ChevronRight size={16} />
                </button>
            </div>

            {isLoading ? (
                <div className={styles.stateCard}>
                    <LoaderCircle size={18} className={styles.spinIcon} />
                    <span>Loading leave summary...</span>
                </div>
            ) : !summaryData ? (
                <div className={styles.stateCard}>No data available.</div>
            ) : summaryData?.isInitialized === false ? (
                <div className={styles.notInitCard}>
                    <AlertTriangle size={18} />
                    <div className={styles.notInitMeta}>
                        <p className={styles.notInitTitle}>No leave balances for {selectedYear}.</p>
                        <p className={styles.notInitSub}>Initialize balances for all active staff to continue.</p>
                    </div>
                    <button type="button" className={styles.primaryBtn} onClick={openInitializeAll}>
                        Initialize All Staff
                        <ArrowRight size={14} />
                    </button>
                </div>
            ) : (
                <>
                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryCard}>
                            <p className={styles.summaryLabel}>Staff Initialized</p>
                            <p className={styles.summaryValue}>{toNumber(summaryData?.totalStaff, 0)}</p>
                            <p className={styles.summarySub}>of {toNumber(summaryData?.totalActiveStaff, 0)} active staff</p>
                        </div>

                        <div className={styles.summaryCard}>
                            <p className={styles.summaryLabel}>PL Days Pending</p>
                            <p className={styles.summaryValue}>{toNumber(summaryData?.totalPLBalanceDays, 0).toFixed(1)}</p>
                            <p className={styles.summarySub}>across initialized staff</p>
                        </div>

                        {summaryData?.encashmentApplicable && (
                            <div className={styles.summaryCard}>
                                <p className={styles.summaryLabel}>Est. PL Liability</p>
                                <p className={styles.summaryValue}>₹{formatAmount(summaryData?.estimatedEncashmentLiability)}</p>
                                <p className={styles.summarySub}>
                                    {toNumber(summaryData?.maxEncashDaysPerYear, 0) > 0
                                        ? `Cap: ${toNumber(summaryData?.maxEncashDaysPerYear, 0)} days/staff`
                                        : 'No yearly encashment cap'}
                                </p>
                            </div>
                        )}
                    </div>

                    {toNumber(summaryData?.uninitializedCount, 0) > 0 && (
                        <div className={styles.banner}>
                            <div className={styles.bannerText}>
                                <AlertTriangle size={15} />
                                <span>
                                    {toNumber(summaryData?.uninitializedCount, 0)} staff have no leave balances for{' '}
                                    {selectedYear}.
                                </span>
                            </div>
                            <button type="button" className={styles.bannerBtn} onClick={openInitializeAll}>
                                Initialize Missing Staff
                                <ArrowRight size={12} />
                            </button>
                        </div>
                    )}

                    <div className={styles.sortCard}>
                        <div className={styles.sortHead}>
                            <ArrowDownUp size={14} />
                            Sort Controls
                        </div>
                        <div className={styles.sortControlsWrap}>
                            <div className={styles.sortOptions}>
                                {SORT_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        className={`${styles.sortBtn} ${
                                            sortBy === option.value ? styles.sortBtnActive : ''
                                        }`}
                                        onClick={() => handleSortChange(option.value)}
                                        title={`Sort by ${option.label}`}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                            <button
                                type="button"
                                className={styles.orderBtn}
                                onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
                            >
                                {sortOrder === 'asc' ? 'A→Z / Low→High' : 'Z→A / High→Low'}
                            </button>
                        </div>
                        <p className={styles.sortHint}>
                            Applied: <strong>{selectedSortLabel}</strong> ({sortOrder.toUpperCase()})
                        </p>
                    </div>

                    {summaryData?.encashmentApplicable && (
                        <div className={styles.liabilityCard}>
                            <div className={styles.liabilityHead}>
                                <Wallet size={14} />
                                <span>PL Encashment Liability</span>
                            </div>
                            <p className={styles.liabilityAmount}>
                                ₹{formatAmount(summaryData?.estimatedEncashmentLiability)}
                            </p>
                            <p className={styles.liabilityNote}>
                                Estimated payout if {toNumber(summaryData?.totalPLBalanceDays, 0).toFixed(1)} PL days
                                are encashed at year-end
                                {toNumber(summaryData?.maxEncashDaysPerYear, 0) > 0
                                    ? ` (capped at ${toNumber(summaryData?.maxEncashDaysPerYear, 0)} days/staff)`
                                    : ''}
                                .
                            </p>
                            <p className={styles.liabilityDisclaimer}>
                                * Estimate based on current basic salaries. Actual may vary.
                            </p>
                            <button
                                type="button"
                                className={styles.yearEndBtn}
                                onClick={() => navigate('/payroll/year-end-leave-process')}
                            >
                                Year-End Processing <ArrowRight size={14} />
                            </button>
                        </div>
                    )}

                    <div className={styles.staffList}>
                        {staffList.map((staff) => (
                            <StaffLeaveRow
                                key={staff?.staffIdentifier}
                                staff={staff}
                                encashmentApplicable={Boolean(summaryData?.encashmentApplicable)}
                                onPress={handleViewLeaveCard}
                                onCredit={handleCreditLeave}
                            />
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

const StaffLeaveRow = ({ staff, encashmentApplicable, onPress, onCredit }) => {
    const leaveChips = [
        { key: 'CL', value: staff?.clRemaining, color: '#1565C0' },
        { key: 'SL', value: staff?.slRemaining, color: '#E65100' },
        { key: 'PL', value: staff?.plRemaining, color: '#2E7D32' },
        { key: 'CO', value: staff?.coRemaining, color: '#6A1B9A' },
    ].filter((chip) => chip.value !== null && chip.value !== undefined)

    return (
        <div className={styles.staffRow}>
            <div className={styles.staffRowLeft}>
                <p className={styles.staffName}>{staff?.staffName || 'Unnamed Staff'}</p>
                <p className={styles.staffDesignation}>{staff?.designationLabel || '—'}</p>
                <div className={styles.chipsRow}>
                    {leaveChips.map((chip) => (
                        <span
                            key={chip.key}
                            className={styles.leaveChip}
                            style={{ backgroundColor: `${chip.color}20`, color: chip.color }}
                        >
                            {chip.key}: {toNumber(chip.value, 0).toFixed(1)}d
                        </span>
                    ))}
                </div>
            </div>

            <div className={styles.staffRowRight}>
                <p className={styles.totalLeave}>{toNumber(staff?.totalLeaveRemaining, 0).toFixed(1)}d total</p>
                {encashmentApplicable && staff?.estimatedEncashLiability !== null && (
                    <p className={styles.encashLiability}>₹{formatAmount(staff?.estimatedEncashLiability)}</p>
                )}
                <div className={styles.rowActions}>
                    <button className={styles.rowActionBtn} type="button" onClick={() => onPress(staff)}>
                        Leave Card →
                    </button>
                    <button className={`${styles.rowActionBtn} ${styles.rowActionBtnPrimary}`} type="button" onClick={() => onCredit(staff)}>
                        Credit Leave
                    </button>
                </div>
            </div>
        </div>
    )
}

export default StoreLeaveBalanceSummaryScreen